import {
  computeSavings,
  type SolarSavings,
} from '@payaca/helpers/solarSavings';
import { type SolarSavingsRegion } from '@payaca/helpers/solarSavingsConstants';

export type { SolarSavings } from '@payaca/helpers/solarSavings';
export {
  SELF_CONSUMPTION_FRACTION,
  SELF_CONSUMPTION_FRACTION_WITH_BATTERY,
  SOLAR_SAVINGS_RATES,
  resolveSolarSavingsRates,
  solarSavingsRegion,
  type SolarSavingsRegion,
} from '@payaca/helpers/solarSavingsConstants';

/** Shared document calculation, with the designer's existing cost argument. */
export const computeSolarSavings = (args: {
  annualAcKwh: number;
  panelsCount: number;
  region: SolarSavingsRegion;
  includeBattery?: boolean;
  systemCostGbp?: number | null;
  selfConsumptionFraction?: number | null;
  retailTariffMinorPerKwh?: number | null;
  exportTariffMinorPerKwh?: number | null;
}): SolarSavings =>
  computeSavings({
    annualAcKwh: args.annualAcKwh,
    panelsCount: args.panelsCount,
    region: args.region,
    includeBattery: args.includeBattery ?? false,
    systemCostMajor: args.systemCostGbp,
    selfConsumptionFraction: args.selfConsumptionFraction,
    retailTariffMinorPerKwh: args.retailTariffMinorPerKwh,
    exportTariffMinorPerKwh: args.exportTariffMinorPerKwh,
  });

/**
 * US self-use share capped at the household's demand: a home cannot use more
 * than it consumes. Undefined without both figures, so the fixed share applies.
 */
export const demandCappedSelfUse = (
  fraction: number,
  annualDemandKwh: number | null,
  annualKwh: number | null
): number | undefined =>
  annualDemandKwh != null && annualKwh != null && annualKwh > 0
    ? Math.min(fraction, annualDemandKwh / annualKwh)
    : undefined;

/**
 * US designs with a monthly credit ledger: its year-1 net benefit is the
 * annual savings figure, and payback follows it.
 */
export const withUsNetMeteringYearOne = (
  savings: SolarSavings | null,
  yearOneBenefit: number | null,
  systemCost: number
): SolarSavings | null =>
  savings && yearOneBenefit != null
    ? {
        ...savings,
        annualSavingsGbp: yearOneBenefit,
        paybackYears: yearOneBenefit > 0 ? systemCost / yearOneBenefit : null,
      }
    : savings;

/**
 * Generation used for the designer's savings and headline kWh. MCS output
 * rounds each roof face to a whole kWh before summing, as the document does.
 *
 * On a UK roof every panel carries the MIS 3002 Appendix B output of its face
 * (kWp × Kk × shade factor), and the document's money follows that total —
 * 4.1.8(b) does not let the satellite-modelled yield outrank the MCS standard
 * estimate. The designer has to agree with the document, so it sums the same
 * field. Any included panel without one (a US roof, or no postcode zone)
 * drops the whole design back to the satellite figure rather than mixing two
 * methods.
 */
export const solarGenerationBasis = (
  panels: ReadonlyArray<{
    annualAcKwh: number;
    mcsYearlyEnergyKwh?: number | null;
    faceIndex?: number;
  }>
): { annualKwh: number; basis: 'mcs' | 'satellite' } => {
  const satellite = panels.reduce((sum, panel) => sum + panel.annualAcKwh, 0);
  if (panels.length === 0) return { annualKwh: satellite, basis: 'satellite' };

  const mcsByFace = new Map<number, number>();
  for (const panel of panels) {
    if (panel.mcsYearlyEnergyKwh == null) {
      return { annualKwh: satellite, basis: 'satellite' };
    }
    const face = panel.faceIndex ?? 0;
    mcsByFace.set(face, (mcsByFace.get(face) ?? 0) + panel.mcsYearlyEnergyKwh);
  }
  const mcs = [...mcsByFace.values()].reduce(
    (sum, annualKwh) => sum + Math.round(annualKwh),
    0
  );
  return { annualKwh: mcs, basis: 'mcs' };
};

/**
 * How the generation figure is described on screen and on the document. A
 * hand-drawn roof has no satellite yield: its MCS figure comes from the drawn
 * pitch and direction on the MCS tables, and anything else is indicative.
 */
export const generationBasisLabel = (
  basis: 'mcs' | 'satellite',
  roof: 'ANALYSIS' | 'MANUAL' = 'ANALYSIS'
): string => {
  if (roof === 'MANUAL') {
    return basis === 'mcs'
      ? 'MCS estimate from the drawn roof and MCS tables'
      : 'Indicative estimate from the drawn roof';
  }
  return basis === 'mcs' ? 'MCS estimate' : 'Satellite estimate';
};
